import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Batch H (final phase) — Keyboard Navigation System, web view only.
 *
 * Scoped to super_admin and deposit_handler — the only two roles with the
 * Deposit/Withdraw/Support/Inquiry multi-section tab switcher these
 * shortcuts jump between. Every shortcut here calls preventDefault() so
 * the browser's own binding for the same combo (e.g. Ctrl+D → bookmark
 * dialog) never fires — some browsers reserve certain combos at the OS/
 * chrome level and won't let a page override them at all; we flag that to
 * the user via a toast instead of failing silently (see the effect below).
 *
 *   Ctrl+D / Ctrl+W / Ctrl+S / Ctrl+I  → jump to Deposit/Withdraw/Support/
 *     Inquiry's main page.
 *   Ctrl+M then a digit (e.g. Ctrl+M, 9) → jump into that MB's group under
 *     Withdraw → Groups, if that MB exists.
 *   Ctrl+G alone → open the "G" all-groups/MBs slider overview.
 *
 * Point 3 (bare Ctrl / bare Tab cycling through the 4 main sections when
 * not inside an open chat) lives locally in AdminDashboard.js /
 * HandlerDashboard.js instead of here, since it needs each dashboard's own
 * "is a chat currently open" state (desktop split-view keeps that as local
 * component state, not reflected in the URL).
 */
const SECTION_KEYS = { d: 'deposit', w: 'withdrawal', s: 'support', i: 'inquiry' };
const MB_DIGIT_TIMEOUT_MS = 1500;
const MB_AMBIGUOUS_WAIT_MS = 600;

const GlobalKeyboardShortcuts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const awaitingMRef = useRef(false);
  const mBufferRef = useRef('');
  const mTimerRef = useRef(null);
  const flaggedUnoverridableRef = useRef(new Set());

  const eligible = user && (user.role === 'super_admin' || user.role === 'deposit_handler');
  const dashboardPath = user?.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard';

  useEffect(() => {
    if (!eligible) return;

    const clearMBuffer = () => {
      awaitingMRef.current = false;
      mBufferRef.current = '';
      if (mTimerRef.current) { clearTimeout(mTimerRef.current); mTimerRef.current = null; }
    };

    const jumpToSection = (section) => {
      if (location.pathname === dashboardPath) {
        window.dispatchEvent(new CustomEvent('dws:kbnav-jump-section', { detail: section }));
      } else {
        navigate(dashboardPath, { state: { activeTab: section, chatTypeTab: section } });
      }
    };

    const resolveMbAndGo = async (mbDigits) => {
      try {
        const res = await axios.get(`${API}/mb-groups/pending-summary`, { params: { user_id: user.id } });
        const groups = res.data?.groups || [];
        const target = groups.find((g) => g.mb_setup === `MB${mbDigits}`);
        if (target?.group_id) {
          navigate(`/groups/${target.group_id}`);
        } else {
          toast.error(`MB${mbDigits} not found`);
        }
      } catch (_) {
        toast.error('Could not look up MB group');
      }
    };

    // A key that a browser refuses to hand to the page at all never fires a
    // keydown event here in the first place — so we can't detect that case
    // directly. What we CAN detect: preventDefault() being a no-op because
    // the browser ignored it (some browsers still dispatch the keydown but
    // also run their native action regardless of preventDefault for a
    // reserved combo). We flag once per combo per session so the user finds
    // out rather than silently losing the shortcut.
    const flagIfPossiblyReserved = (comboLabel) => {
      if (flaggedUnoverridableRef.current.has(comboLabel)) return;
      flaggedUnoverridableRef.current.add(comboLabel);
      // Heuristic: browsers commonly reserve Ctrl+T/N/W (tab/window
      // management) at the OS level regardless of what the page does.
      if (['Ctrl+W'].includes(comboLabel)) {
        toast(`Heads up: ${comboLabel} may be reserved by your browser (e.g. "close tab") and could occasionally not reach the app.`, { duration: 6000 });
      }
    };

    const onKeyDown = (e) => {
      // Digit follow-up for Ctrl+M — checked first since by the time the
      // user types the digit, Ctrl may already be released.
      if (awaitingMRef.current && /^[0-9]$/.test(e.key)) {
        e.preventDefault();
        mBufferRef.current += e.key;
        const buf = mBufferRef.current;
        if (buf === '7' || buf === '8' || buf === '9' || buf === '10' || buf === '11') {
          resolveMbAndGo(buf);
          clearMBuffer();
        } else if (buf === '1') {
          // Only "10"/"11" are 2-digit MBs — wait briefly for a possible
          // 2nd digit before resolving lone "1" (MB1 doesn't exist).
          if (mTimerRef.current) clearTimeout(mTimerRef.current);
          mTimerRef.current = setTimeout(() => {
            resolveMbAndGo('1');
            clearMBuffer();
          }, MB_AMBIGUOUS_WAIT_MS);
        } else {
          toast.error(`MB${buf} not found`);
          clearMBuffer();
        }
        return;
      }

      if (!e.ctrlKey || e.metaKey || e.altKey) return;
      const key = e.key.toLowerCase();

      if (SECTION_KEYS[key]) {
        e.preventDefault();
        flagIfPossiblyReserved(`Ctrl+${key.toUpperCase()}`);
        jumpToSection(SECTION_KEYS[key]);
        return;
      }
      if (key === 'm') {
        e.preventDefault();
        awaitingMRef.current = true;
        mBufferRef.current = '';
        if (mTimerRef.current) clearTimeout(mTimerRef.current);
        mTimerRef.current = setTimeout(clearMBuffer, MB_DIGIT_TIMEOUT_MS);
        return;
      }
      if (key === 'g') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('dws:kbnav-open-g'));
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearMBuffer();
    };
  }, [eligible, user, navigate, location.pathname, dashboardPath]);

  return null;
};

export default GlobalKeyboardShortcuts;
